// Components==============
import { faChevronLeft, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import destroyDream from 'actions/dream/destroy';
import storeDream from 'actions/dream/store';
import updateDream from 'actions/dream/update';
import Button from 'components/UI/Button/Button';
import CircleButton from 'components/UI/CircleButton/CircleButton';
import SkillModal from 'components/UI/SkillModal/SkillModal';
import { useConfirm } from 'hooks/useConfirm';
import { useMediaQ } from 'hooks/useMediaQ';
import { usePermission } from 'hooks/usePermission';
import { useReduxAction } from 'hooks/useReduxAction';
import { useRootModal } from 'hooks/useRootModal';
import { useTranslate } from 'hooks/useTranslate';
import AddDreamModal from 'pages/Growth/GrowthPortfolio/components/Dreams/AddDreamModal/AddDreamModal';
import React, { useContext } from 'react';
import { GlobalRootDreamContext } from 'utilities/GlobalContext/GlobalRootDream';
import { DreamModalContext } from '../DreamModal';
import './Top.module.scss';
// =========================

export default function Top() {
  const { setRootDreamModal, rootDreamModal } = useContext(
    GlobalRootDreamContext
  );
  const {
    originSkill,
    handleClose,
    editing,
    setEditing,
    originState,
    setOrigin,
    dream,
    editState,
    setEditState,
    defaultState,
  } = useContext(DreamModalContext);

  const dispatch = useReduxAction();

  function updateDreamModal(result: DreamEntity) {
    setRootDreamModal((prev) =>
      prev ? { ...prev, dream: result } : undefined
    );

    return setEditing(false);
  }

  const dreamAccomplished =
    dream?.requirements.find((r) => (r?.done || 0) < r.amount) === undefined;

  const translate = useTranslate();
  const { setModal } = useRootModal();

  const isTeacher = usePermission(['Docent', 'super-admin']);

  const query = useMediaQ('min', 525);

  const confirm = useConfirm();

  const addTemplateCondition =
    (originState === 'template' ||
      originState === 'person' ||
      originState === 'skill') &&
    dream;

  function handleBack() {
    if (originState === 'skill') {
      return setModal(<SkillModal selectedSkill={originSkill} />);
    }

    if (originState === 'view') {
      return handleClose();
    }

    if (originState === 'person') {
      return handleClose();
    }

    if (originState === 'add-dream' || originState === 'template') {
      setRootDreamModal(undefined);
      return setModal(<AddDreamModal />);
    }
  }

  function handleDelete() {
    confirm(translate('dream.confirm-delete'), () => {
      dispatch(destroyDream(dream?.id));
      return handleClose();
    });
  }

  function handleChange() {
    if (originState === 'add-dream') {
      setRootDreamModal(undefined);
      return setModal(<AddDreamModal />);
    }
    if (originState === 'view') {
      setEditState(defaultState);
      setEditing((prev) => !prev);
    }
  }

  function handleSubmit() {
    // Add new dreaam
    if (originState === 'add-dream' && editing) {
      return dispatch(
        storeDream({
          ...editState,
          image: undefined,
        })
      ).then((result: DreamEntity) => {
        setOrigin('view');
        updateDreamModal(result);
      });
    }

    // Edit dream
    if (originState === 'view' && editing) {
      return dispatch(
        updateDream({ ...editState, id: dream?.id })
      ).then((result: DreamEntity) => updateDreamModal(result));
    }

    // Toggle finished value
    if (originState === 'view') {
      return dispatch(
        updateDream({
          ...dream,
          finished: dream?.finished ? 0 : 1,
        })
      ).then((result: DreamEntity) => updateDreamModal(result));
    }

    // Add template to my dreams
    if (addTemplateCondition) {
      return dispatch(
        storeDream({
          copyDream: true,
          id: dream?.id,
        })
      ).then((result: DreamEntity) => {
        handleClose();

        if (rootDreamModal?.dream?.id === dream?.id) {
          setTimeout(() => {
            setRootDreamModal({ origin: 'view', dream: result, edit: true });
          }, 500);
        }
      });
    }
  }

  return (
    <div styleName="wrapper">
      {editing ? (
        <div />
      ) : (
        <Button color="grey-black" onClick={handleBack}>
          <FontAwesomeIcon icon={faChevronLeft} />{' '}
          {translate(
            originState === 'skill'
              ? 'general.terms.skill'
              : originState === 'template'
              ? 'dream.terms.pick-template'
              : 'general.terms.back'
          )}
        </Button>
      )}
      {!isTeacher && (
        <div styleName="top-right-buttons">
          {originState === 'view' && editing && (
            <>
              {query ? (
                <Button onClick={handleDelete} color="white-red">
                  {translate('general.terms.delete')}
                </Button>
              ) : (
                <CircleButton
                  icon={faTrash}
                  onClick={handleDelete}
                  color="red"
                />
              )}
            </>
          )}
          {(originState === 'view' || editing) && (
            <Button onClick={handleChange} color="white">
              {translate(
                editing ? 'general.terms.cancel' : 'general.terms.update'
              )}
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            color={
              originState === 'view' && !editing && !dreamAccomplished
                ? 'inactive'
                : originState === 'view' && !editing && dream?.finished
                ? 'green'
                : editing
                ? 'blue'
                : 'white'
            }
          >
            {translate(
              editing
                ? 'general.terms.save'
                : addTemplateCondition
                ? 'dream.actions.add-dream'
                : originState === 'view'
                ? 'dream.terms.dream-accomplished'
                : 'general.terms.save'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
